import { Box, Text } from "@mantine/core";
import {
  IconAmbulance,
  IconFileDollar,
  IconRoute,
  IconTruckDelivery,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import DateFormat from "types/DateFormat";
import { dateToUTC } from "utils/date";

const ActivityStepInFinnish = {
  LOADING: "Lastaus",
  UNLOADING: "Purku",
} as const;

const ChatPopoverHeading = ({
  activityKind,
  bookingId,
  time,
  tripId,
}: {
  activityKind?: string;
  time?: string;
  tripId?: string;
  bookingId?: string;
}) => {
  if (activityKind === ActivityStepInFinnish.UNLOADING) {
    return <UnloadingTitle time={time} />;
  }

  if (activityKind === ActivityStepInFinnish.LOADING) {
    return <LoadingTitle time={time} />;
  }

  if (bookingId) {
    return <BookingTitle bookingId={bookingId} />;
  }

  return <TransportTitle tripId={tripId} />;
};

const BookingTitle = ({ bookingId }: { bookingId: string }) => {
  const { t } = useTranslation("discussions");

  return (
    <Box className="flex items-center gap-2 shrink-0">
      <Box className="h-8 w-8 bg-white flex items-center justify-center rounded-full shrink-0">
        <IconFileDollar height={24} width={24} color="var(--color-gray-dark)" />
      </Box>
      <Box className="flex flex-row min-w-0 ml-3.5 gap-1">
        <Text fz={14} fw={600} className="truncate text-white">
          {t("bookingId")}
        </Text>
        <Text fz={14} fw={400} className="truncate text-white">
          {bookingId}
        </Text>
      </Box>
    </Box>
  );
};

const TransportTitle = ({ tripId }: { tripId?: string }) => {
  const { t } = useTranslation("discussions");

  return (
    <Box className="flex items-center gap-2 shrink-0">
      <Box className="h-8 w-8 bg-white flex items-center justify-center rounded-full shrink-0">
        <IconRoute
          height={24}
          width={24}
          color="var(--color-gray-dark)"
          className="shrink-0"
        />
      </Box>
      <Box className="flex flex-row min-w-0 ml-3.5 gap-1">
        <Text fz={14} fw={600} className="truncate text-white">
          {t("transport")}
        </Text>
        {tripId && (
          <Text fz={14} fw={400} className="truncate text-white">
            {tripId}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export const UnloadingTitle = ({ time }: { time?: string }) => {
  const { t } = useTranslation("discussions");

  return (
    <Box className="flex items-center gap-2 shrink-0">
      <Box className="h-8 w-8 bg-white flex items-center justify-center rounded-full shrink-0">
        <IconTruckDelivery color="var(--color-red-dark)" />
      </Box>
      <Box className="flex flex-row min-w-0 ml-3.5 gap-1">
        <Text fz={14} fw={600} className="truncate text-white">
          {t("unloading")}
        </Text>
        {time && (
          <Text fz={14} fw={400} className="truncate text-white">
            {dateToUTC(time).format(DateFormat.DEFAULT)}
          </Text>
        )}
      </Box>
    </Box>
  );
};

const LoadingTitle = ({ time }: { time?: string }) => {
  const { t } = useTranslation("discussions");

  return (
    <Box className="flex items-center gap-2 shrink-0">
      <Box className="h-8 w-8 bg-white flex items-center justify-center rounded-full shrink-0">
        <IconAmbulance color="var(--color-green)" />
      </Box>
      <Box className="flex flex-row min-w-0 ml-3.5 gap-1">
        <Text fz={14} fw={600} className="truncate text-white">
          {t("loading")}
        </Text>
        {time && (
          <Text fz={14} fw={400} className="truncate text-white">
            {dateToUTC(time).format(DateFormat.DEFAULT)}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default ChatPopoverHeading;
